import { ref, set, get, getDatabase } from 'firebase/database';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { trackImageUpload } from './analyticsService';
import { getAuth } from 'firebase/auth';

interface ImageUploadResult {
  url: string;
  originalName: string;
  size: number;
  type: string;
}

// Compressed as hard as reasonable: images live as base64 strings inside the
// RTDB `menu` node (free tier — no Cloud Storage bucket), so every kilobyte
// saved here shrinks the public menu payload and speeds up mobile loads.
const options = {
  maxSizeMB: 0.8,
  maxWidthOrHeight: 900,
  useWebWorker: true
};

/**
 * Images are stored as base64 data URLs inside Realtime Database. A small
 * deterministic hash maps a data URL back to its `menu-images/{uuid}` record
 * so images can still be deleted by content alone (see deleteImage).
 */
const hashString = (str: string): string => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(16);
};

export const optimizeAndUploadImage = async (file: File): Promise<ImageUploadResult> => {
  const originalName = file.name;
  try {
    const compressedFile = await imageCompression(file, options);
    const imageId = uuidv4();
    const type = compressedFile.type;
    const size = compressedFile.size;

    const reader = new FileReader();
    const base64Promise = new Promise<string>((resolve, reject) => {
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(compressedFile);
    });
    const base64Data = await base64Promise;

    const db = getDatabase();
    const imageRef = ref(db, `menu-images/${imageId}`);
    const maxRetries = 3;
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        await set(imageRef, {
          data: base64Data,
          name: file.name,
          type,
          size,
          uploadedBy: getAuth().currentUser?.uid ?? 'unknown',
          uploadedAt: Date.now()
        });
        break;
      } catch (error) {
        retryCount++;
        if (retryCount === maxRetries) throw error;
        await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
      }
    }

    // Index the upload by a hash of its content so a later deleteImage()
    // call (which only receives the data URL) can find the record again.
    // A failed index write must not fail the upload itself.
    try {
      await set(ref(db, `menu-images-index/${hashString(base64Data)}`), {
        imageId,
        name: file.name,
        size,
        uploadedAt: Date.now()
      });
    } catch (error) {
      console.warn('Image uploaded but its deletion index could not be written:', error);
    }

    await trackImageUpload(true, size, type);
    return { url: base64Data, originalName, size, type };
  } catch (error) {
    console.error('Error uploading image:', error);
    await trackImageUpload(
      false,
      file.size,
      file.type,
      error instanceof Error ? error.message : 'Unknown error'
    );
    throw error;
  }
};

/**
 * `imageRefOrDataUrl` is either the base64 data URL that callers store on
 * `item.image`, or a `menu-images/{uuid}` key (legacy callers).
 */
export const deleteImage = async (imageRefOrDataUrl: string): Promise<void> => {
  if (!imageRefOrDataUrl) return;

  try {
    const db = getDatabase();

    if (imageRefOrDataUrl.startsWith('data:')) {
      const indexKey = hashString(imageRefOrDataUrl);
      const indexSnap = await get(ref(db, `menu-images-index/${indexKey}`));
      const entry = indexSnap.val() as { imageId?: string } | null;

      if (entry?.imageId) {
        await set(ref(db, `menu-images/${entry.imageId}`), null);
      } else {
        console.warn('No menu-images index entry found for this data URL; nothing to delete.');
      }
      // Clean up the index entry as well (no-op if it never existed).
      await set(ref(db, `menu-images-index/${indexKey}`), null);
      return;
    }

    await set(ref(db, `menu-images/${imageRefOrDataUrl}`), null);
  } catch (error) {
    console.error('Error deleting image:', error);
    throw error;
  }
};